<template>
  <div class="expense-documentation" redesign>
    <!-- Request stepper -->
    <RequestStepProgressBar step6 />

    <div class="form-container expense-documentation-form">
      <!-- Information -->
      <div v-if="totalAmount && totalAmount <= 200" class="form-header">
        <span v-translate
          >Since you are requesting $200 or less, you do not have to upload expense
          documentation.</span
        >
      </div>
      <div v-if="totalAmount && totalAmount > 200" class="form-header">
        <span v-translate>
          In order to receive grant funds, you need to document all expenses (with the exception of
          food, if you request $200 or less in food expenses).
        </span>
      </div>

      <!-- Sample documentation -->
      <div class="form-field document-information-container">
        <h4 class="form-field-header" v-translate>Your documents need to show:</h4>
        <div class="expense-documentation-row-wrapper">
          <div class="form-field-container sample-document-container">
            <img :src="require('@/assets/images/Sample Document - Document your emergency.svg')" />
          </div>
          <div v-if="organizationDetails.documentationCopy" v-html="documentationCopy" class="form-field-container"></div>
        </div>
      </div>

      <!-- Information card -->
      <div class="form-field info-container">
        <div class="tooltip-icon">
          <img src="@/assets/icons/information-icon.png" />
        </div>
        <div class="tooltip">
          <span class="tooltip-text" v-translate>
            If the document is not in your name, but you are responsible for the expense, you must
            document your connection to the person whose name is on the document. If it is in the
            name of someone in your household, please submit a document to show how you are related
            (i.e. a tax return, marriage license, or a bill with both your names and shared address
            on it).
          </span>
        </div>
      </div>

      <!-- Document uploads for each expense type -->
      <div v-for="(expense, i) in expenses" :key="i" class="expense-uploads">
        <DocumentUpload
          :expense="expense"
          :documentList="expenseDocs[expense.id]"
          :loading="loading"
          @documents-changed="onDocumentsChanged($event, expense.id)"
          @delete-document="onDeleteDocument($event, expense.id)"
          :actionName="`upload-expense-${expense.categoryRef.humanReadableId}-document`"
        />
      </div>

      <!-- Additional information -->
      <div class="form-field request-questions">
        <h4 class="form-field-header" v-translate>
          Please add any additional information that will help us understand your document(s):
        </h4>
        <div class="form-field-container" id="md-textarea">
          <textarea
            class="form-field-textarea"
            @click="mixin_TextAreaResize_resize"
            @input="mixin_TextAreaResize_resize"
            v-model="currentRequest.expenseDocumentationMisc"
            @blur="saveFieldUpdate"
            data-action="input-expense-notes"
          />
        </div>
      </div>

      <!-- Next step -->
      <div class="form-footer">
        <div class="next-step">
          <translate>Next step:</translate><br /><translate class="next-step-name"
            >View request summary</translate
          >
        </div>
      </div>

      <!-- Submit -->
      <SubmitButton
        :onContinue="confirmDocs"
        :loading="loading"
        actionName="commit-expense-documents"
      />
    </div>

    <!-- Modals -->

    <!-- Grant limit modal -->
    <Modal
      v-if="isModalOpen && !acceleratedModalOpen"
      :additionalContainerStyles="{ padding: '15vh 0' }"
      dimmed
      :width="100"
      @close-modal="closeModal"
    >
      <div class="form-container card-shape modal-window">
        <div class="form-field">
          <p>
            <translate>
              The amount you requested exceeds the grant limit. Good news; there’s no need to change
              it. However, you only need to provide
            </translate>
            <span>
              <strong>
                {{ $gettext('documentation for') }}
                {{ organizationDetails.grantLimit | currencyDollars }}
                {{ $gettext('of expenses') }}.
              </strong>
            </span>
          </p>
        </div>

        <div class="form-footer">
          <div class="button-container">
            <!--<div>&nbsp;</div>-->
            <SubmitButton
              data-action="commit-partial-expense-docs"
              :onContinue="closeModal()"
              :loading="loading"
              :noNavigation="true"
            />
          </div>
        </div>
      </div>
    </Modal>

    <!-- Documentation confirmation modal -->
    <Modal
      :additionalModalStyles="{
        width: '100%',
        'max-width': '500px',
        border: 'none',
      }"
      v-if="docConfirmationModal"
      dimmed
    >
      <div class="form-container card-shape modal-window">
        <div class="form-field">
          <h4 class="form-field-header" v-translate>Please confirm that your document(s):</h4>
          <div class="form-field-container">
            <ul>
              <li v-translate>
                Show a dollar amount more than or equal to the grant amount you are requesting
              </li>
              <li v-translate>Show your name (or that of someone in your household)</li>
              <li v-translate>Show a bill date or payment due date</li>
              <li v-translate>Show the biller's name and information</li>
              <li v-translate>Are readable, not blurry</li>
              <li v-translate>
                Are complete; include the entire page, including headers and footers
              </li>
              <li v-translate>blackOutPersonalInformation</li>
            </ul>
          </div>
        </div>
        <div class="form-footer">
          <div class="button-container frow" style="justify-content: space-between">
            <button @click="docConfirmationModal = false" class="form-button white-button fcolumn">
              <span v-translate>Go back</span>
            </button>

            <SubmitButton
              data-action="confirm-expense-documents"
              :onContinue="() => {
                docConfirmationModal = false
                onNext()
              }"
              :loading="loading"
            />
          </div>
        </div>
      </div>
    </Modal>

    <!-- Accelerated modal -->
    <Modal
      :additionalModalStyles="{
        width: '100%',
        'max-width': '500px',
        border: 'none',
        overflow: 'hidden',
      }"
      v-if="
        acceleratedModalOpen &&
        organizationDetails.expenseDocumentationEnabled &&
        organizationDetails.eventDocumentationEnabled
      "
      dimmed
    >
      <div class="form-container card-shape modal-window">
        <div class="form-field">
          <p v-translate>
            Great news! You can skip this step. Proceed to the final step to review your grant
            request summary.
          </p>
        </div>
        <div class="button-container" style="justify-content: space-between">
          <button @click="acceleratedModalOpen = false" class="form-button white-button">
            <span v-translate>Stay here</span>
          </button>

          <SubmitButton
            buttonText="Continue"
            :onContinue="onCommonCategoryModalClose"
            :loading="loading"
            data-action="skip-expense-docs"
          />
        </div>
      </div>
    </Modal>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import TextAreaResize from '../../mixins/TextAreaResize'
import Request from '@/services/requests'
import Document from '@/services/documents'
import { analyticsMixin } from '@/services/googleAnalytics'
import DocumentUpload from '@/components/requests/DocumentUpload'
import LoadSpinner from '@/components/LoadSpinner'
import Modal from '@/components/Modal'
import RequestStepProgressBar from '@/components/stepper/RequestStepProgressBar'
import SubmitButton from '@/components/requests/SubmitButton'
import UserEvent from '@/services/userEvents'
import { formatText, formatValue } from '@/services/utils'
import EventCategory from '@/services/eventCategories'

export default {
  name: 'ExpenseDocumentation',
  mixins: [analyticsMixin, TextAreaResize],
  components: {
    DocumentUpload,
    LoadSpinner,
    Modal,
    RequestStepProgressBar,
    SubmitButton,
  },
  beforeRouteLeave(to, from, next) {
    this.$store.commit('updateUncommonExpenseCheck', null)
    this.$store.commit('updateCommonExpenseCheck', null)
    next()
  },

  data() {
    return {
      expenses: [],
      expenseDocs: null,
      killList: [],
      loading: false,
      isModalOpen: false,
      placeholder: this.$gettext('Answer'),
      acceleratedModalOpen: false,
      expenseSkipped: false,
      docConfirmationModal: false,
    }
  },
  async created() {
    // Pull current Request, to make sure we have the most up-to-date data
    const currentRequest = await Request.api.retrieve(this.currentRequest.id)
    this.$store.dispatch('updateCurrentRequest', currentRequest)

    // Create an object of all expense documents:
    //  - Each key is the ID of an expense
    //  - Each value is an array of documents for that expense
    this.setDocs()
    this.startEditingPing()
    this.isModalOpen = this.showGrantLimitCopy
    this.shouldSkip()
  },
  mounted() {
    // Update the current request completion status
    UserEvent.api.requestUpdateCompletionStatus(
      this.currentRequest.id,
      UserEvent.options.REQUEST_STEP_EXPENSE_DOCUMENTATION,
    )
  },
  destroyed() {
    clearInterval(this.interval)
  },
  methods: {
    confirmDocs() {
      if (
        Object.values(this.expenseDocs).every(expenseDoc => expenseDoc.length == 0) ||
        this.totalAmount <= 200 ||
        this.expenseSkipped
      ) {
        this.onNext()
      } else {
        this.docConfirmationModal = true
        return Promise.reject('confirm-modal')
      }
    },
    closeModal() {
      this.isModalOpen = false
    },
    setDocs() {
      const expenseDocs = {}
      this.currentRequest.expensesRef.forEach(expense => {
        expenseDocs[expense.id] = [...expense.documentsRef]
      })
      this.expenseDocs = expenseDocs
      this.expenses = this.currentRequest.expensesRef.slice()
    },
    async onDocumentsChanged(documentList, expenseId) {
      this.loading = true

      this.eventsTracker('uploaded-or-changed-upload-for-expense', `Expense ID: ${expenseId}`)
      // Replace all documents for this particular expense with the newest set
      this.expenseDocs[expenseId] = documentList
      await this.updateDocuments()
      this.loading = false
    },
    async onDeleteDocument(docToDelete, expenseId) {
      this.loading = true
      // If a document the user wants to delete has an ID, it means it exists in the database.
      // That means we need to keep track of it, and send a command to delete it later.
      if (docToDelete.id) {
        this.killList.push(docToDelete)
      }
      this.expenseDocs[expenseId] = this.expenseDocs[expenseId].filter(
        doc => doc.id !== docToDelete.id,
      )

      await this.updateDocuments()
      this.loading = false
    },
    async updateDocuments() {
      // Collect expense-docs into an array of arrays
      const documentList = Object.values(this.expenseDocs)

      // Loop through all documents from each expense
      const promiseList = []
      documentList.forEach(expenseDocs => {
        expenseDocs.forEach(doc => {
          const index = expenseDocs.indexOf(doc)

          // If document does not have an ID, it doesn't yet exist in the DB - so we need to create it
          if (!doc.id) {
            promiseList.push(
              Document.api.create(doc).then(document => (expenseDocs[index] = document)),
            )
          }
        })
      })
      // Delete every document the user removed
      this.killList.forEach(doc => {
        promiseList.push(Document.api.delete(doc))
      })

      // Wait for all Documents to be modified before updating Request
      await Promise.all(promiseList)
      this.killList = []

      // Modify the request in state
      const request = this.currentRequest
      request.expensesRef.forEach(expense => {
        expense.documentsRef = this.expenseDocs[expense.id]
      })
      this.$store.dispatch('updateCurrentRequest', request)
      this.setDocs()
    },
    async onNext() {
      this.loading = true
      this.trackClickedCTA()
      // Collect expense-docs into an array of arrays
      const documentList = Object.values(this.expenseDocs)

      // Loop through all documents from each expense
      const promiseList = []
      documentList.forEach(expenseDocs => {
        expenseDocs.forEach(doc => {
          // If document does not have an ID, it doesn't yet exist in the DB - so we need to create it
          if (!doc.id) {
            promiseList.push(Document.api.create(doc))
          }
        })
      })

      // Delete every document the user removed
      this.killList.forEach(doc => {
        promiseList.push(Document.api.delete(doc))
      })

      // Wait for all Documents to be modified before updating Request
      await Promise.all(promiseList)

      // Update Request and push next screen
      const updateFields = ['expenseDocumentationMisc']
      const response = await Request.api.update(this.currentRequest, updateFields)
      await this.$store.dispatch('updateCurrentRequest', response)

      this.loading = false
    },
    async saveFieldUpdate() {
      const updateFields = ['expenseDocumentationMisc']
      const response = await Request.api.update(this.currentRequest, updateFields)
      await this.$store.dispatch('updateCurrentRequest', response)
    },
    startEditingPing() {
      const request = this.currentRequest
      Request.api.editingPing(request)
      this.interval = setInterval(function() {
        Request.api.editingPing(request)
      }, 10000)
    },
    onCommonCategoryModalClose() {
      this.acceleratedModalOpen = false
      if (this.expenseSkipped) {
        this.onNext()
      }
    },
    shouldSkip() {
      const allHaveDocs = this.currentRequest.expensesRef.every(
        (expense) => expense.documentsRef.length > 0,
      )

      if(!this.requestHasUncommonEventCategories && !this.hasEventCategoryWithExpenseDocumentation) {
        this.acceleratedModalOpen = true
        if (this.acceleratedModalOpen) {
          this.trackCustomPageview('ExpenseDocPopupSkip', '/expense-doc-popup-skip')
          this.isModalOpen = false
          this.expenseSkipped = true
          return 'skip'
        }
      } else if (
        this.requestHasCommonExpenseCategories &&
        (!this.requestHasUncommonExpenseCategories ||
          (this.requestHasUncommonExpenseCategories && allHaveDocs))
      ) {
        this.acceleratedModalOpen = this.currentRequest.expensesRef.length > 0
        if (this.acceleratedModalOpen) {
          this.trackCustomPageview('ExpenseDocPopupSkip', '/expense-doc-popup-skip')
          this.isModalOpen = false
          this.expenseSkipped = true
          return 'skip'
        }
      }
      this.acceleratedModalOpen = false
    },
  },
  computed: {
    ...mapState([
      'currentRequest',
      'user',
      'organizationDetails',
      'requestHasUncommonExpenseCategories',
      'requestHasCommonExpenseCategories',
      'requestHasUncommonEventCategories',
      'defaultRequestFlow',
      'reproductiveRightsFlow',
    ]),
    disabled() {
      return this.loading
    },
    totalAmount() {
      if (!this.expenses) return 0
      return this.expenses.reduce((acc, i) => {
        return Number(i.amount) + acc
      }, 0)
    },
    showGrantLimitCopy() {
      let orgHasGrantLimit = !!this.organizationDetails.grantLimit
      if (orgHasGrantLimit) {
        let requestingMoreThanGrantLimit = this.totalAmount > this.organizationDetails.grantLimit
        let requestingMoreThan200 = this.totalAmount > 200
        return requestingMoreThanGrantLimit && requestingMoreThan200
      }
      return false
    },
    documentationCopy() {
      const varMap = { org: this.organizationDetails }
      return formatValue(this.organizationDetails.documentationCopy, varMap)
    },
    hasEventCategoryWithExpenseDocumentation() {
      return this.currentRequest.eventsRef.some(event => EventCategory.options.EXPENSE_DOCUMENTATION_REQUIRED.includes(event.categoryRef.requiredDocumentation))
    }
  },
}
</script>

<style lang="scss" scoped>
@import '@/styles/variables';
@import '@/styles/mixins/buttons';
@import '@/styles/mixins/containers';
@import '@/styles/flex-box';
@import '@/styles/text';
@import '@/styles/grant-circles';

.expense-documentation {
  display: flex;
  flex-direction: column;

  .expense-documentation-form {
    @extend .constrain-tablet-and-up;

    @media (min-width: 1140px) {
      max-width: 1076px !important;
    }
  }

  @include tablet-and-up-screen {
    .expense-documentation-row-wrapper {
      display: flex;
      flex-direction: row;

      .sample-document-container {
        min-width: 320px;
      }
    }
  }
}

.document-information-container {
  @extend .card-shape;

  background-color: var(--light-brand-color);
  box-shadow: none;
  -webkit-box-shadow: none;

  .form-field-header {
    font-size: 1.5rem;
    line-height: 2rem;
  }

  .sample-document-container {
    text-align: center;
    margin-top: 1.5rem;
    margin-bottom: 1rem;

    img {
      width: 80%;
    }

    @include mobile-screen {
      img {
        max-width: 320px;
      }
    }

    @include tablet-and-up-screen {
      margin-top: 1rem;

      img {
        min-width: 266px;
      }
    }

    @include tablet-screen {
      text-align: left;
    }

    @include desktop-screen {
      margin-right: 2rem !important;
    }
  }

  .document-requirements-list {
    padding: 0;
    list-style: none;

    li {
      margin-left: 0;
      margin-bottom: 1rem;

      &:before {
        background-color: #212b36;
        border-radius: 50%;
        color: $white;
        content: var(--n);
        display: inline-block;
        height: 22px;
        text-align: center;
        width: 23px;
        vertical-align: top;
      }

      span {
        display: inline-block;
        padding-left: 0.75rem;
        width: 90%;
        font-size: 1.125rem;
        line-height: 1.5rem;
        font-weight: bold;
      }
    }
  }
}
</style>
